import React, { useState, useEffect, useCallback } from 'react';
import {
    UserPlus, Filter, AlertCircle, Calendar, DollarSign, MapPin,
    CheckCircle, XCircle, Clock, FileText, Mail, Phone, Briefcase, Loader
} from 'lucide-react';
import applicationService from '../../../services/applicationService';
import { useNotification } from '../../../App';

const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    return new Intl.NumberFormat('en-KE', {
        style: 'currency', currency: 'KES', minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(amount);
};

const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
        case 'approved': return <CheckCircle className="h-5 w-5 text-highland-500" />;
        case 'rejected': return <XCircle className="h-5 w-5 text-murram-500" />;
        default: return <Clock className="h-5 w-5 text-dawn-600" />;
    }
};

const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
        case 'approved': return 'bg-highland-50 text-highland-600';
        case 'rejected': return 'bg-murram-100 text-murram-600';
        default: return 'bg-dawn-100/40 text-dawn-600';
    }
};

/**
 * ManagerApplications Component
 * Applications received against the manager's properties
 */
const ManagerApplications = () => {
    const { addNotification } = useNotification();

    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statusFilter, setStatusFilter] = useState(null);

    const [actingId, setActingId] = useState(null);
    const [rejectingId, setRejectingId] = useState(null);
    const [rejectReason, setRejectReason] = useState('');

    const fetchApplications = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await applicationService.getLandlordApplications(statusFilter);
            setApplications(data.applications || []);
        } catch (err) {
            setError(err.message || 'Failed to load applications');
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => {
        fetchApplications();
    }, [fetchApplications]);

    const applyLocalDecision = (applicationId, status, reason) => {
        setApplications((prev) => prev.map((app) =>
            app.applicationId === applicationId
                ? { ...app, status, decisionReason: reason || null, decisionDate: new Date().toISOString() }
                : app
        ));
    };

    const handleApprove = async (applicationId) => {
        setActingId(applicationId);
        try {
            await applicationService.decideApplication(applicationId, { status: 'approved' });
            applyLocalDecision(applicationId, 'approved');
            addNotification('Application approved', 'success');
        } catch (err) {
            addNotification(err.message || 'Failed to approve application', 'error');
        } finally {
            setActingId(null);
        }
    };

    const handleConfirmReject = async (applicationId) => {
        setActingId(applicationId);
        try {
            await applicationService.decideApplication(applicationId, { status: 'rejected', reason: rejectReason.trim() });
            applyLocalDecision(applicationId, 'rejected', rejectReason.trim());
            addNotification('Application rejected', 'info');
            setRejectingId(null);
            setRejectReason('');
        } catch (err) {
            addNotification(err.message || 'Failed to reject application', 'error');
        } finally {
            setActingId(null);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-karibu-100">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-highland-500 mx-auto mb-4"></div>
                    <p className="text-soil-600">Loading applications...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-karibu-100">
                <div className="text-center bg-white p-8 rounded-lg shadow-md max-w-md">
                    <AlertCircle className="h-12 w-12 text-murram-500 mx-auto mb-4" />
                    <h2 className="font-display text-xl font-semibold text-soil-900 mb-2">Error Loading Applications</h2>
                    <p className="text-soil-600 mb-4">{error}</p>
                    <button
                        onClick={fetchApplications}
                        className="px-4 py-2 bg-highland-500 text-white rounded-lg hover:bg-highland-600"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-karibu-100 pt-20 pb-8">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="mb-8">
                    <h1 className="font-display text-3xl font-bold text-soil-900">Applications</h1>
                    <p className="mt-2 text-soil-600">
                        Review applications submitted for your properties ({applications.length} total)
                    </p>
                </div>

                <div className="bg-white rounded-lg shadow-sm p-4 mb-6 border border-soil-100">
                    <div className="flex items-center space-x-2">
                        <Filter className="h-5 w-5 text-soil-400" />
                        <select
                            value={statusFilter || 'all'}
                            onChange={(e) => setStatusFilter(e.target.value === 'all' ? null : e.target.value)}
                            className="px-4 py-2 border border-soil-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-highland-500"
                        >
                            <option value="all">All Applications</option>
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                        </select>
                    </div>
                </div>

                {applications.length > 0 ? (
                    <div className="space-y-4">
                        {applications.map((application) => (
                            <div
                                key={application.applicationId}
                                className="bg-white rounded-lg shadow-sm border border-soil-100 overflow-hidden"
                            >
                                <div className="p-6">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-3 mb-2">
                                                <h3 className="text-lg font-semibold text-soil-900">
                                                    {application.applicant?.name || 'Applicant'}
                                                </h3>
                                                <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(application.status)}`}>
                                                    {application.status?.toUpperCase()}
                                                </span>
                                            </div>
                                            <div className="flex items-center flex-wrap gap-4 text-sm text-soil-600">
                                                {application.applicant?.email && (
                                                    <span className="flex items-center"><Mail className="h-4 w-4 mr-1" />{application.applicant.email}</span>
                                                )}
                                                {application.applicant?.phone && (
                                                    <span className="flex items-center"><Phone className="h-4 w-4 mr-1" />{application.applicant.phone}</span>
                                                )}
                                                {application.applicant?.occupation && (
                                                    <span className="flex items-center"><Briefcase className="h-4 w-4 mr-1" />{application.applicant.occupation}</span>
                                                )}
                                            </div>
                                            <div className="flex items-center text-sm text-soil-400 mt-2">
                                                <MapPin className="h-4 w-4 mr-1" />
                                                <span>{application.property?.propertyName || `Property #${application.propertyId}`}</span>
                                            </div>
                                        </div>
                                        <div className="flex-shrink-0 ml-4">
                                            {getStatusIcon(application.status)}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                        <div>
                                            <p className="text-xs text-soil-400 mb-1">Desired Move-in</p>
                                            <div className="flex items-center text-sm font-medium text-soil-900">
                                                <Calendar className="h-4 w-4 mr-1 text-soil-400" />
                                                {application.desiredMoveInDate ? new Date(application.desiredMoveInDate).toLocaleDateString() : 'N/A'}
                                            </div>
                                        </div>
                                        <div>
                                            <p className="text-xs text-soil-400 mb-1">Lease Length</p>
                                            <p className="text-sm font-medium text-soil-900">
                                                {application.desiredLeaseLength ? `${application.desiredLeaseLength} months` : 'N/A'}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-soil-400 mb-1">Proposed Rent</p>
                                            <div className="flex items-center text-sm font-medium text-soil-900">
                                                <DollarSign className="h-4 w-4 mr-1 text-soil-400" />
                                                {formatCurrency(application.desiredRentAmount)}
                                            </div>
                                        </div>
                                    </div>

                                    {application.message && (
                                        <div className="mb-4 p-3 bg-soil-50 rounded-lg flex items-start space-x-2">
                                            <FileText className="h-4 w-4 text-soil-400 mt-0.5 flex-shrink-0" />
                                            <p className="text-sm text-soil-600">{application.message}</p>
                                        </div>
                                    )}

                                    {application.decisionReason && (
                                        <div className="mb-4 p-3 bg-soil-50 rounded-lg">
                                            <p className="text-xs text-soil-400 mb-1">Decision Reason</p>
                                            <p className="text-sm text-soil-600">{application.decisionReason}</p>
                                        </div>
                                    )}

                                    {application.status?.toLowerCase() === 'pending' && (
                                        <div className="border-t border-soil-100 pt-4">
                                            {rejectingId === application.applicationId ? (
                                                <div className="space-y-3">
                                                    <textarea
                                                        rows={2}
                                                        value={rejectReason}
                                                        onChange={(e) => setRejectReason(e.target.value)}
                                                        placeholder="Reason for rejection (optional)"
                                                        className="w-full px-3 py-2 border border-soil-100 rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent text-sm"
                                                    />
                                                    <div className="flex items-center space-x-2">
                                                        <button
                                                            onClick={() => handleConfirmReject(application.applicationId)}
                                                            disabled={actingId === application.applicationId}
                                                            className="px-4 py-2 bg-murram-500 text-white text-sm font-medium rounded-lg hover:bg-murram-600 disabled:opacity-50 flex items-center space-x-2"
                                                        >
                                                            {actingId === application.applicationId ? <Loader className="h-4 w-4 animate-spin" /> : <span>Confirm Reject</span>}
                                                        </button>
                                                        <button
                                                            onClick={() => { setRejectingId(null); setRejectReason(''); }}
                                                            className="px-4 py-2 border border-soil-100 text-soil-600 text-sm font-medium rounded-lg hover:bg-soil-50"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="flex items-center space-x-3">
                                                    <button
                                                        onClick={() => handleApprove(application.applicationId)}
                                                        disabled={actingId === application.applicationId}
                                                        className="px-4 py-2 bg-highland-500 text-white text-sm font-medium rounded-lg hover:bg-highland-600 disabled:opacity-50 flex items-center space-x-2"
                                                    >
                                                        {actingId === application.applicationId ? <Loader className="h-4 w-4 animate-spin" /> : <span>Approve</span>}
                                                    </button>
                                                    <button
                                                        onClick={() => setRejectingId(application.applicationId)}
                                                        disabled={actingId === application.applicationId}
                                                        className="px-4 py-2 border border-soil-100 text-soil-600 text-sm font-medium rounded-lg hover:bg-soil-50 disabled:opacity-50"
                                                    >
                                                        Reject
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-soil-100">
                        <UserPlus className="mx-auto h-12 w-12 text-soil-400 mb-4" />
                        <h3 className="font-display text-lg font-medium text-soil-900 mb-2">
                            {statusFilter ? 'No applications found' : 'No applications yet'}
                        </h3>
                        <p className="text-soil-600">
                            {statusFilter ? 'Try adjusting your filter' : 'Applications for your properties will show up here'}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ManagerApplications;
